import { useSuspenseQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { SubNavAppBar } from "components/AppBar";
import { WasteDateRangePicker } from "components/WasteDateRangePicker";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

import { prettyPrintWasteViewType } from "./prettyPrintWasteViewType";
import { useWasteFacility } from "./useWasteFacility";
import { DEFAULT_WASTE_FACILITY_VIEW, useWasteFacilityState } from "./WasteFacilityContext";

type GraphType = "hazardousWaste" | "hazardousWasteByMonth" | "highestGeneratingWasteStreams";

export type Filter = {
  graphType: GraphType;
  startDate?: string;
  endDate?: string;
  tags?: string[];
  view?: FacilityWasteView;
};

const TAGS_QUERY = gql(/* GraphQL */ `
  query WasteTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

export function WasteFacilityFilters() {
  const theme = useTheme();
  const { tenantId } = useTenant();
  const { facilityId } = useParams<{ facilityId: string }>();
  const initialLoad = useRef(true);

  const [defaultDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>(() => {
    const currentYear = new Date().getFullYear();
    const startDate = new Date(currentYear, 0, 1).toISOString().slice(0, 10);
    const endDate = new Date(currentYear, 11, 31).toISOString().slice(0, 10);
    return { startDate, endDate };
  });

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const {
    wasteFacilityState: { optimisticFilters },
    setWasteFacilityState,
  } = useWasteFacilityState();

  const { data: tagsData } = useSuspenseQuery(TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:Waste`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
  });

  const { data: facilityData } = useWasteFacility(facilityId);

  const tagOptions = useMemo(() => tagsData?.tags?.items ?? [], [tagsData]);

  // Use optimistic filters for immediate UI feedback
  const { tags, startDate, endDate, view } = optimisticFilters;

  /**
   * Seed the date range when the URL arrives without one, and only the date
   * range. Every date-scoped query on the page skips until both ends are set,
   * so without this the shipment chart sits on its loading skeleton.
   *
   * Deliberately narrower than a check for "any filter at all". `view` always
   * has a value now, because the provider supplies the default before anything
   * reads it, so an any-filter check would always see something and never
   * seed the dates. Writing the whole default set would also overwrite a view
   * the URL asked for explicitly.
   */
  useEffect(() => {
    if (!startDate || !endDate) {
      if (!initialLoad.current) return;
      initialLoad.current = false;
      setWasteFacilityState((f) => {
        f.startDate = defaultDateRange.startDate;
        f.endDate = defaultDateRange.endDate;
      });
    }
  }, [startDate, endDate, setWasteFacilityState, defaultDateRange]);

  // prevent the user from changing the date range outside of the current year
  // and prevent end date from going past the end of the current month
  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diff = end.getTime() - start.getTime();
      const diffInDays = diff / (1000 * 60 * 60 * 24);

      // Get the last day of the current month
      const now = new Date();
      const lastDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      // If end date is beyond the current month, cap it to the last day of current month
      if (end > lastDayOfCurrentMonth) {
        setWasteFacilityState((f) => {
          f.endDate = lastDayOfCurrentMonth.toISOString().slice(0, 10);
        });
        return;
      }

      if (diffInDays >= 366) {
        // Use Luxon to safely add 1 year (automatically handles leap year edge cases)
        const startDateTime = DateTime.fromJSDate(start);
        const newEndDateTime = startDateTime.plus({ years: 1 });

        setWasteFacilityState((f) => {
          f.endDate = newEndDateTime.toISODate();
        });
      }
    }
  }, [startDate, endDate, setWasteFacilityState, defaultDateRange]);

  // Handlers
  const handleViewChange = (newView: FacilityWasteView) => {
    setWasteFacilityState((f) => {
      f.view = newView;
    });
  };

  const handleDateRangeChange = (next: { startDate?: string; endDate?: string }) => {
    setWasteFacilityState((f) => {
      f.startDate = next.startDate;
      f.endDate = next.endDate;
    });
  };

  const standardInputProps = {
    size: "small" as const,
    fullWidth: true,
    sx: {
      "& .MuiInputBase-root": {
        height: "40px", // Standardize height across all inputs
      },
    },
  };

  const facility = facilityData.facility;

  return (
    <SubNavAppBar
      sx={{
        px: theme.spacing(2),
        py: theme.spacing(3),
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
        justifyContent: "space-between",
        flexDirection: { lg: "row" },
        gap: 1,
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
        <WasteDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onAccept={handleDateRangeChange}
        />

        {/* View Filter */}
        <FormControl
          sx={{ ...standardInputProps.sx, width: { xs: "100%", md: 210 } }}
          variant="standard"
        >
          <InputLabel id="view-filter-label">View</InputLabel>
          <Select
            labelId="view-filter-label"
            value={view ?? DEFAULT_WASTE_FACILITY_VIEW}
            onChange={(e) => handleViewChange(e.target.value)}
            {...standardInputProps}
            label="View"
          >
            <MenuItem value={FacilityWasteView.All}>
              {prettyPrintWasteViewType(FacilityWasteView.All)}
            </MenuItem>
            <MenuItem value={FacilityWasteView.Reportable}>
              {prettyPrintWasteViewType(FacilityWasteView.Reportable)}
            </MenuItem>
            <MenuItem value={FacilityWasteView.NotReportable}>
              {prettyPrintWasteViewType(FacilityWasteView.NotReportable)}
            </MenuItem>
          </Select>
        </FormControl>

        {/* Tags */}
        <Box sx={{ alignItems: "end" }}>
          <Chip
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>{(tags?.length ?? 0) > 0 ? `Tags (${tags?.length ?? 0})` : "Tags"}</span>
                <ExpandMoreIcon fontSize="small" />
              </Stack>
            }
            color={menuAnchor ? "primary" : "default"}
            variant="filled"
            sx={{
              ...standardInputProps.sx,
              mt: { xs: 0, md: "20px" },
            }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            clickable
            size="medium"
          />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {tagOptions.length === 0 && <MenuItem disabled={true}>No tags available</MenuItem>}
            {tagOptions.length > 0 && (
              <MenuItem
                value="select-all"
                onClick={() => {
                  const isAllSelected = (tags?.length ?? 0) === tagOptions.length;
                  setWasteFacilityState((f) => {
                    f.tags = isAllSelected ? [] : tagOptions.map((tag) => tag.name);
                  });
                  setMenuAnchor(null);
                }}
              >
                <Checkbox
                  checked={tags?.length === tagOptions.length}
                  indeterminate={(tags?.length ?? 0) > 0 && (tags?.length ?? 0) < tagOptions.length}
                />
                Select All
              </MenuItem>
            )}
            {tagOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.name}
                onClick={() => {
                  const isSelected = tags?.includes(option.name);
                  setWasteFacilityState((f) => {
                    const current = new Set(f.tags ?? []);
                    if (isSelected) {
                      current.delete(option.name);
                    } else {
                      current.add(option.name);
                    }
                    f.tags = Array.from(current);
                  });
                }}
              >
                <Checkbox checked={!!tags?.includes(option.name)} /> {option.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Stack>
      <Stack alignItems="flex-end" spacing={0.5}>
        <Tooltip
          title={
            facility?.mostRecentVendorManifestShippedDate
              ? `Vendor: most recent shipment for this facility ${prettyPrintDateMed(
                  facility?.mostRecentVendorManifestShippedDate
                )}`
              : ""
          }
        >
          <Typography variant="caption" fontStyle="italic">
            {facility?.vendorWasteManifestLastUpdated
              ? `Vendor data updated ${prettyPrintDateMed(facility.vendorWasteManifestLastUpdated)}`
              : "No vendor data for this facility"}
          </Typography>
        </Tooltip>

        <Tooltip
          title={
            facility?.mostRecentEManifestShippedDate
              ? `e‑Manifest: most recent shipment for this facility ${prettyPrintDateMed(
                  facility?.mostRecentEManifestShippedDate
                )}`
              : ""
          }
        >
          <Typography variant="caption" fontStyle="italic">
            {facility?.eManifestLastUpdated
              ? `e‑Manifest data updated ${prettyPrintDateMed(facility.eManifestLastUpdated)}`
              : "No e‑Manifest data for this facility"}
          </Typography>
        </Tooltip>
      </Stack>
    </SubNavAppBar>
  );
}
